import { type VariantProps, cva } from 'class-variance-authority'

import * as React from 'react'

import { AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react'

import { cn } from '@/lib/utils'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

const alertVariants = cva('relative w-full rounded-lg border px-4 py-3 text-sm', {
  variants: {
    variant: {
      default: 'bg-background text-foreground border-border',
      destructive:
        'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive bg-destructive/5',
      warning:
        'border-orange-200 bg-orange-50 text-orange-800 [&>svg]:text-orange-600 dark:border-orange-200/30 dark:bg-orange-950/20 dark:text-orange-200 dark:[&>svg]:text-orange-400',
      info: 'border-blue-200 bg-blue-50 text-blue-800 [&>svg]:text-blue-600 dark:border-blue-200/30 dark:bg-blue-950/20 dark:text-blue-200 dark:[&>svg]:text-blue-400',
      success:
        'border-green-200 bg-green-50 text-green-800 [&>svg]:text-green-600 dark:border-green-200/30 dark:bg-green-950/20 dark:text-green-200 dark:[&>svg]:text-green-400',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const titleVariants = cva('font-medium leading-none tracking-tight', {
  variants: {
    variant: {
      default: 'text-foreground',
      destructive: 'text-destructive dark:text-red-400',
      warning: 'text-orange-900 dark:text-orange-100',
      info: 'text-blue-900 dark:text-blue-100',
      success: 'text-green-900 dark:text-green-100',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const descriptionVariants = cva('text-sm [&_p]:leading-relaxed', {
  variants: {
    variant: {
      default: 'text-muted-foreground',
      destructive: 'text-destructive/90 dark:text-red-300',
      warning: 'text-orange-800/90 dark:text-orange-200/90',
      info: 'text-blue-800/90 dark:text-blue-200/90',
      success: 'text-green-800/90 dark:text-green-200/90',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const iconMap = {
  default: AlertCircle,
  destructive: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle,
} as const

export interface SmartAlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode
  description?: React.ReactNode
  icon?: React.ComponentType<{ className?: string }> | false
  actions?: React.ReactNode
}

export const SmartAlert = React.forwardRef<HTMLDivElement, SmartAlertProps>(
  ({ className, variant = 'default', title, description, icon, actions, children, ...props }, ref) => {
    // Determine which icon to use
    const IconComponent = icon === false ? null : icon || iconMap[variant || 'default']

    return (
      <Alert ref={ref} className={cn(alertVariants({ variant }), className)} {...props}>
        {/* Icon */}
        {IconComponent && <IconComponent className='h-4 w-4' />}

        {/* Content */}
        <div className='flex-1 space-y-2'>
          {title && <AlertTitle className={cn(titleVariants({ variant }), 'text-[1rem]')}>{title}</AlertTitle>}

          {(description || children) && (
            <AlertDescription className={cn(descriptionVariants({ variant }))}>
              {description || children}
            </AlertDescription>
          )}

          {/* Actions */}
          {actions && <div className='mt-3 flex flex-wrap gap-2'>{actions}</div>}
        </div>
      </Alert>
    )
  }
)

SmartAlert.displayName = 'SmartAlert'

// Convenience components for common alert types
export const InfoAlert = React.forwardRef<HTMLDivElement, Omit<SmartAlertProps, 'variant'>>((props, ref) => (
  <SmartAlert {...props} ref={ref} variant='info' />
))
InfoAlert.displayName = 'InfoAlert'

export const WarningAlert = React.forwardRef<HTMLDivElement, Omit<SmartAlertProps, 'variant'>>((props, ref) => (
  <SmartAlert {...props} ref={ref} variant='warning' />
))
WarningAlert.displayName = 'WarningAlert'

export const SuccessAlert = React.forwardRef<HTMLDivElement, Omit<SmartAlertProps, 'variant'>>((props, ref) => (
  <SmartAlert {...props} ref={ref} variant='success' />
))
SuccessAlert.displayName = 'SuccessAlert'

export const DestructiveAlert = React.forwardRef<HTMLDivElement, Omit<SmartAlertProps, 'variant'>>((props, ref) => (
  <SmartAlert {...props} ref={ref} variant='destructive' />
))
DestructiveAlert.displayName = 'DestructiveAlert'

export const DefaultAlert = React.forwardRef<HTMLDivElement, Omit<SmartAlertProps, 'variant'>>((props, ref) => (
  <SmartAlert {...props} ref={ref} variant='default' />
))
DefaultAlert.displayName = 'DefaultAlert'
